import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    LuArrowUpRight, LuBox, LuChevronLeft, LuCircleAlert, LuCreditCard,
    LuArrowRight, LuInfo, LuLoaderCircle, LuMessageSquare, LuMinus, LuPlug, LuPlus, LuQrCode, LuUsers, LuX,
} from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import Dialog from './Dialog';
import {
    billingService, motivoSemTroca, rotuloProvedor,
    type AssinaturaBackend, type PlanoBackend,
} from '../services/billingService';
import GerenciarEmpresaService from '../services/GerenciarEmpresaService';
import { mascararCpfCnpj } from '../services/OrganizationService';
import './PlanoModal.css';

/**
 * Assinatura: escolher plano e contratar, em duas telas.
 *
 * Desenho fechado de 2026-09-12 (`docs/designsystem/propostas/plano.html`),
 * segunda versão — a primeira, com três direções e um painel de consumo, foi
 * recusada por inteiro: *"achei extremamente poluído e complexo o que vc fez de
 * colocar o que já usou"*, *"temos que ser simples nessa etapa"*.
 *
 * Por que modal centralizada e não painel lateral: a decisão de 2026-09-10
 * ("nunca modal centralizada") vale para **formulário de registro** — ver/editar
 * um cadastro. Isto é **fluxo**, e o precedente é o `NovoAgenteModal`. Largura e
 * raio saem de lá para as duas serem a mesma coisa.
 *
 * O que deliberadamente NÃO está aqui, porque ele cortou: medidor de consumo,
 * coluna "cabe no seu uso?", tabela comparativa. Do diagnóstico sobrou **uma
 * linha** no card, e só quando ela evita um clique que não funciona.
 */

type Tela = 'planos' | 'confirmar';
type FormaPagamento = 'CARD' | 'PIX' | 'CARD_INSTALLMENT';

interface PlanoModalProps {
    open: boolean;
    onClose: () => void;
    /** Recarrega quem abriu (a página `/plano`) depois de uma mudança. */
    onMudou?: () => void;
}

const brl = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const CICLO: Record<string, string> = {
    WEEKLY: 'por semana', MONTHLY: 'por mês', QUARTERLY: 'por trimestre',
    SEMIANNUALLY: 'por semestre', ANNUALLY: 'por ano', TRIAL: 'gratuito',
};

/** O resumo pede o adjetivo ("Cobrança: mensal"), não o complemento — tirar o
 *  "por " de "por mês" deixava "Cobrança: mês". */
const CICLO_ADJ: Record<string, string> = {
    WEEKLY: 'semanal', MONTHLY: 'mensal', QUARTERLY: 'trimestral',
    SEMIANNUALLY: 'semestral', ANNUALLY: 'anual', TRIAL: 'gratuito',
};

const usuariosTxt = (n: number) => `${n} ${n === 1 ? 'usuário' : 'usuários'}`;

const PlanoModal = ({ open, onClose, onMudou }: PlanoModalProps) => {
    const [tela, setTela] = useState<Tela>('planos');
    const [planos, setPlanos] = useState<PlanoBackend[]>([]);
    const [assinatura, setAssinatura] = useState<AssinaturaBackend | null>(null);
    const [usuariosAtivos, setUsuariosAtivos] = useState<number | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [escolhido, setEscolhido] = useState<PlanoBackend | null>(null);
    const [forma, setForma] = useState<FormaPagamento>('CARD');
    const [quantidade, setQuantidade] = useState(1);
    const [processando, setProcessando] = useState(false);
    const [confirmarTroca, setConfirmarTroca] = useState(false);
    /** CPF/CNPJ de quem paga, com máscara. Vem do CNPJ da empresa quando ela tem. */
    const [documento, setDocumento] = useState('');

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const [resPlanos, resAss] = await Promise.all([
                billingService.buscarPlanosDisponiveis(),
                billingService.buscarAssinaturaAtiva().catch(() => null),
            ]);
            setPlanos(resPlanos.sucesso ? resPlanos.dados : []);
            setAssinatura(resAss?.sucesso ? resAss.dados : null);
        } catch {
            aviso.erro('Não foi possível carregar os planos.');
        } finally {
            setCarregando(false);
        }
        /* A contagem de usuários só serve de piso para a quantidade contratada —
           a API recusa contratar menos usuários do que os ativos. Se falhar, o
           piso vira 1 e quem barra é a API; nunca derruba o modal. */
        try {
            const r = await fetch(
                `${import.meta.env.VITE_API_URL || ''}/api/Usuario/BuscarTodos`,
                { credentials: 'include' },
            );
            const dados = await r.json();
            const lista = dados?.dados ?? dados;
            if (Array.isArray(lista)) setUsuariosAtivos(lista.length);
        } catch { /* silencioso de propósito */ }
        /* Pré-preenche o documento do PIX. O cadastro não pede CNPJ, então o
           comum é vir vazio; se falhar, a pessoa digita. */
        try {
            const dados = await GerenciarEmpresaService.getDadosGerais();
            setDocumento(mascararCpfCnpj(dados?.cnpj ?? ''));
        } catch { /* silencioso de propósito */ }
    }, []);

    useEffect(() => {
        if (!open) return;
        setTela('planos');
        setEscolhido(null);
        setForma('CARD');
        carregar();
    }, [open, carregar]);

    useEffect(() => {
        if (!open) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', aoTeclar);
        return () => document.removeEventListener('keydown', aoTeclar);
    }, [open, onClose]);

    const temAssinaturaViva = !!assinatura
        && assinatura.assinaturaStatus !== 'CANCELLED'
        && assinatura.assinaturaStatus !== 'EXPIRED';
    const ehTrial = assinatura?.assinaturaStatus === 'TRIAL';
    const impedimentoDeTroca = motivoSemTroca(assinatura?.assinaturaMetodo);

    /** Trocar de plano usa o gateway gravado na ASSINATURA, não o do plano novo
     *  (`AssinaturaService.TrocarPlano`) — então plano de outro provedor não é
     *  alcançável por troca, e a API responde "plano não sincronizado", que
     *  aponta para o problema errado. */
    const provedorCruzado = useCallback((p: PlanoBackend) =>
        temAssinaturaViva && !ehTrial
        && !!assinatura?.assinaturaProvedorPagamento
        && p.planoProvedorPagamento !== assinatura.assinaturaProvedorPagamento,
    [assinatura, temAssinaturaViva, ehTrial]);

    /** A única linha que sobrou do diagnóstico, um motivo de cada vez. */
    const motivoBloqueio = useCallback((p: PlanoBackend): string | null => {
        if (provedorCruzado(p)) {
            return `Este plano é cobrado pelo ${rotuloProvedor(p.planoProvedorPagamento)} e sua assinatura`
                + ` está no ${rotuloProvedor(assinatura?.assinaturaProvedorPagamento)}.`
                + ' Para mudar, cancele a atual e contrate este.';
        }
        return null;
    }, [provedorCruzado, assinatura]);

    const recursos = (p: PlanoBackend) =>
        (p.planoDescricao || '').split(',').map(s => s.trim()).filter(Boolean);

    const quantidadeMinima = Math.max(1, usuariosAtivos ?? 1);

    const abrirConfirmacao = (p: PlanoBackend) => {
        setEscolhido(p);
        setForma('CARD');
        /* Numa troca parte do que já está contratado; numa contratação, do
           mínimo que a empresa já usa. O trial não conta: ele libera só 1. */
        const base = trocaEPossivel ? (assinatura?.assinaturaQuantidadeUsuarios ?? 1) : 1;
        setQuantidade(Math.max(base, quantidadeMinima));
        setTela('confirmar');
    };

    const contratar = async () => {
        if (!escolhido) return;
        setProcessando(true);
        try {
            const res = forma === 'PIX'
                ? await billingService.criarCheckoutPix(escolhido.planoId, quantidade, pedeDocumento ? digitosDocumento : null)
                : forma === 'CARD_INSTALLMENT'
                ? await billingService.criarCheckoutParcelado(escolhido.planoId, quantidade)
                : await billingService.criarCheckout(escolhido.planoId, quantidade);

            if (res.sucesso && res.dados?.assinaturaCheckoutUrl) {
                window.open(res.dados.assinaturaCheckoutUrl, '_blank', 'noopener,noreferrer');
                onMudou?.();
                onClose();
            } else {
                aviso.erro(res.mensagem || 'Não foi possível gerar o pagamento.');
            }
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível gerar o pagamento.'));
        } finally {
            setProcessando(false);
        }
    };

    const trocar = async () => {
        if (!escolhido) return;
        setConfirmarTroca(false);
        setProcessando(true);
        try {
            const res = soQuantidade
                ? await billingService.alterarQuantidadeUsuarios(quantidade)
                : await billingService.trocarPlano(escolhido.planoId, quantidade);
            if (!res.sucesso) { aviso.erro(res.mensagem || 'Não foi possível concluir a alteração.'); return; }
            /* Na Asaas a troca cancela a assinatura atual e devolve um checkout
               novo — sem abrir, a empresa fica sem cobrança e sem saber. */
            if (res.dados?.checkoutUrl) {
                aviso.atencao(soQuantidade
                    ? 'Confirme o pagamento da nova quantidade de usuários para efetivar.'
                    : 'Confirme o pagamento do novo plano para efetivar a troca.');
                window.open(res.dados.checkoutUrl, '_blank', 'noopener,noreferrer');
            } else {
                aviso.ok(res.mensagem || 'Troca solicitada. Vale a partir do próximo ciclo.');
            }
            onMudou?.();
            onClose();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível concluir a alteração.'));
        } finally {
            setProcessando(false);
        }
    };

    const ehPlanoAtual = (p: PlanoBackend) => temAssinaturaViva && assinatura?.planoId === p.planoId;
    /* Trial contrata pelo checkout normal: a assinatura de teste não tem
       recorrência real para migrar. */
    const trocaEPossivel = temAssinaturaViva && !ehTrial && !impedimentoDeTroca;

    /* Mesmo plano, só a quantidade muda — é o `AlterarQuantidadeUsuarios`, não
       uma troca de plano. */
    const soQuantidade = trocaEPossivel && !!escolhido && assinatura?.planoId === escolhido.planoId;
    const quantidadeAtual = assinatura?.assinaturaQuantidadeUsuarios ?? 1;
    const semMudanca = soQuantidade && quantidade === quantidadeAtual;

    /* O PIX do Asaas não é hospedado: o Asaas recusa a cobrança se o cliente
       não tiver CPF/CNPJ (AssinaturaService.CriarCheckoutPix). A AbacatePay
       coleta na própria página. */
    const pedeDocumento = !trocaEPossivel && forma === 'PIX'
        && escolhido?.planoProvedorPagamento === 'ASAAS';
    const digitosDocumento = documento.replace(/\D/g, '');
    const documentoCompleto = digitosDocumento.length === 11 || digitosDocumento.length === 14;

    const totalHoje = useMemo(
        () => escolhido ? brl(escolhido.planoPreco * quantidade) : '',
        [escolhido, quantidade],
    );

    /* A largura segue o conteúdo em vez de um 880px fixo. Na tela de planos ela
       depende de quantos vieram (248px por card + 12 de gap + 40 de padding),
       com teto de 880 para não virar faixa em catálogo grande; na de confirmar
       é a soma das duas colunas. Sem isto, 2 planos esticavam cada card para
       414px e o cartão ficava com mais moldura que conteúdo. */
    const largura = useMemo(() => {
        if (tela === 'confirmar') return '700px';
        const n = Math.max(planos.length, 1);
        return `min(880px, ${n * 248 + (n - 1) * 12 + 40}px)`;
    }, [tela, planos.length]);

    if (!open) return null;

    /* Portal no body: a sidebar é quem abre o modal, e ela vive num slot com
       contexto de empilhamento próprio — sem o portal o conteúdo da página
       ficava POR CIMA do overlay, e o scrim não cobria nada. */
    return createPortal(
        <div className="pln-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="pln" role="dialog" aria-modal="true"
                style={{ '--pln-w': largura } as React.CSSProperties}
                aria-label={tela === 'planos' ? 'Escolher plano' : 'Confirmar assinatura'}>

                <div className="pln-head">
                    {tela === 'confirmar' && (
                        <button type="button" className="pln-back" aria-label="Voltar aos planos"
                            onClick={() => setTela('planos')}>
                            <LuChevronLeft size={14} />
                        </button>
                    )}
                    <span className="pln-title">
                        <LuBox size={16} />
                        {tela === 'planos'
                            ? 'Escolher plano'
                            : soQuantidade
                            ? 'Alterar usuários'
                            : `Assinar o plano ${escolhido?.planoNome ?? ''}`}
                    </span>
                    <button type="button" className="pln-x" aria-label="Fechar" onClick={onClose}>
                        <LuX size={16} />
                    </button>
                </div>

                {carregando ? (
                    <div className="pln-carregando"><LuLoaderCircle size={18} className="pln-girando" /> Carregando…</div>
                ) : tela === 'planos' ? (
                    <div className="pln-body">
                        {planos.length === 0 ? (
                            <div className="pln-vazio">
                                <LuBox size={22} />
                                <b>Nenhum plano disponível no momento</b>
                                {assinatura?.nomePlano
                                    ? <>Sua conta está no plano <b>{assinatura.nomePlano}</b>. Para mudar de plano, fale com o suporte.</>
                                    : <>Para contratar um plano, fale com o suporte.</>}
                                <div className="pln-vazio-bt">
                                    <a className="pln-btn" href="/help">
                                        <LuMessageSquare size={14} /> Falar com o suporte
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="pln-cards">
                                {planos.map(p => {
                                    const atual = ehPlanoAtual(p);
                                    const bloqueio = atual ? null : motivoBloqueio(p);
                                    return (
                                        <div key={p.planoId} className={`pln-card ${atual ? 'is-atual' : ''}`}>
                                            <span className="pln-mk"><LuBox size={16} /></span>
                                            <div className="pln-nm">{p.planoNome}</div>
                                            <div className="pln-pr">{brl(p.planoPreco)}</div>
                                            <div className="pln-cy">por usuário, {CICLO[p.planoCiclo] ?? 'por ciclo'}</div>

                                            <div className="pln-bt">
                                                {atual && trocaEPossivel ? (
                                                    <button type="button" className="pln-btn"
                                                        disabled={processando}
                                                        onClick={() => abrirConfirmacao(p)}>
                                                        <LuUsers size={13} /> Alterar usuários
                                                    </button>
                                                ) : atual ? (
                                                    <span className="pln-btn is-off">Plano atual</span>
                                                ) : (
                                                    <button type="button"
                                                        className={`pln-btn ${bloqueio ? 'is-off' : 'is-pri'}`}
                                                        disabled={!!bloqueio || processando}
                                                        onClick={() => abrirConfirmacao(p)}>
                                                        {trocaEPossivel ? 'Mudar para este' : 'Contratar'}
                                                    </button>
                                                )}
                                            </div>

                                            <ul className="pln-fx">
                                                <li className="is-lim"><LuUsers size={13} />
                                                    {atual
                                                        ? `${usuariosTxt(quantidadeAtual)} contratados`
                                                        : 'Você escolhe quantos usuários'}
                                                </li>
                                                <li className="is-lim"><LuPlug size={13} />
                                                    {p.planoLimiteConexao == null
                                                        ? 'Conexões ilimitadas'
                                                        : `${p.planoLimiteConexao} ${p.planoLimiteConexao === 1 ? 'conexão' : 'conexões'} por plataforma`}
                                                </li>
                                                {recursos(p).map((r, i) => (
                                                    <li key={i}><span className="pln-tick" aria-hidden="true" />{r}</li>
                                                ))}
                                            </ul>

                                            {bloqueio && (
                                                <div className="pln-nao"><LuCircleAlert size={13} />{bloqueio}</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : escolhido && (
                    <div className="pln-2col">
                        <div className="pln-esq">
                            <div className="pln-esc">
                                <span className="pln-mk"><LuBox size={18} /></span>
                                <div>
                                    <div className="pln-esc-nm">{escolhido.planoNome}</div>
                                    <div className="pln-esc-cy">
                                        {brl(escolhido.planoPreco)} por usuário, {CICLO[escolhido.planoCiclo] ?? ''}
                                        {' · '}via {rotuloProvedor(escolhido.planoProvedorPagamento)}
                                    </div>
                                </div>
                                <button type="button" className="pln-btn pln-btn-auto"
                                    onClick={() => setTela('planos')}>Mudar plano</button>
                            </div>

                            <div className="pln-sect"><LuUsers size={14} /> Usuários</div>
                            <div className="pln-qtd">
                                <button type="button" className="pln-qtd-bt" aria-label="Menos um usuário"
                                    disabled={quantidade <= quantidadeMinima}
                                    onClick={() => setQuantidade(q => Math.max(quantidadeMinima, q - 1))}>
                                    <LuMinus size={14} />
                                </button>
                                <input className="pln-qtd-in" type="number" inputMode="numeric"
                                    min={quantidadeMinima} value={quantidade}
                                    aria-label="Quantidade de usuários"
                                    onChange={e => {
                                        const v = Math.floor(Number(e.target.value));
                                        setQuantidade(Number.isFinite(v) ? Math.max(1, v) : 1);
                                    }}
                                    onBlur={() => setQuantidade(q => Math.max(quantidadeMinima, q))} />
                                <button type="button" className="pln-qtd-bt" aria-label="Mais um usuário"
                                    onClick={() => setQuantidade(q => q + 1)}>
                                    <LuPlus size={14} />
                                </button>
                                <span className="pln-qtd-tx">
                                    {usuariosAtivos != null
                                        ? `Sua empresa tem ${usuariosTxt(usuariosAtivos)} ativos — esse é o mínimo.`
                                        : 'Cada usuário ativo ocupa uma licença.'}
                                </span>
                            </div>

                            {trocaEPossivel ? (
                                <>
                                    {/* Numa troca, o que a pessoa precisa ver é o que sai e o que
                                        entra. Sem isto a coluna ficava com 89px de vão (medido) e
                                        o modal não dizia de onde estava saindo. */}
                                    <div className="pln-sect"><LuBox size={14} /> O que muda</div>
                                    <div className="pln-muda">
                                        <div className="pln-muda-lado">
                                            <span className="pln-muda-k">Hoje</span>
                                            <span className="pln-muda-nm">{assinatura?.nomePlano}</span>
                                            <span className="pln-muda-vl">
                                                {usuariosTxt(quantidadeAtual)}
                                                {' · '}{assinatura ? brl(assinatura.assinaturaValor) : ''}
                                                {' '}{CICLO[assinatura?.assinaturaCiclo ?? ''] ?? ''}
                                            </span>
                                        </div>
                                        <LuArrowRight size={16} className="pln-muda-seta" />
                                        <div className="pln-muda-lado is-novo">
                                            <span className="pln-muda-k">Passa a ser</span>
                                            <span className="pln-muda-nm">{escolhido.planoNome}</span>
                                            <span className="pln-muda-vl">
                                                {usuariosTxt(quantidade)}
                                                {' · '}{totalHoje} {CICLO[escolhido.planoCiclo] ?? ''}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="pln-nota">
                                        <LuInfo size={14} />
                                        <span>
                                            {assinatura?.assinaturaProvedorPagamento === 'ASAAS'
                                                ? `A assinatura atual será cancelada e você precisará confirmar o pagamento ${soQuantidade ? 'da nova quantidade' : 'do novo plano'}. Até a confirmação, a empresa fica sem cobrança ativa.`
                                                : 'A mudança vale a partir do próximo ciclo de cobrança. Nada é cobrado agora.'}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="pln-sect"><LuCreditCard size={14} /> Forma de pagamento</div>
                                    <button type="button" className={`pln-opt ${forma === 'CARD' ? 'is-on' : ''}`}
                                        onClick={() => setForma('CARD')} aria-pressed={forma === 'CARD'}>
                                        <span className="pln-rd" />
                                        <span className="pln-opt-ic"><LuCreditCard size={16} /></span>
                                        <span className="pln-opt-tx">
                                            <b>Cartão de crédito</b>
                                            <span>Cobrança automática a cada ciclo</span>
                                        </span>
                                    </button>
                                    <button type="button" className={`pln-opt ${forma === 'PIX' ? 'is-on' : ''}`}
                                        onClick={() => setForma('PIX')} aria-pressed={forma === 'PIX'}>
                                        <span className="pln-rd" />
                                        <span className="pln-opt-ic"><LuQrCode size={16} /></span>
                                        <span className="pln-opt-tx">
                                            <b>PIX</b>
                                            <span>Pagamento avulso por QR Code, renovação manual</span>
                                        </span>
                                    </button>
                                    {pedeDocumento && (
                                        <label className="pln-doc">
                                            <span className="pln-doc-k">CPF ou CNPJ de quem paga</span>
                                            <input className="pln-doc-in" type="text" inputMode="numeric"
                                                autoComplete="off" placeholder="000.000.000-00"
                                                value={documento}
                                                onChange={e => setDocumento(mascararCpfCnpj(e.target.value))} />
                                            <span className="pln-qtd-tx">
                                                O Asaas exige o documento para gerar o PIX.
                                            </span>
                                        </label>
                                    )}
                                    {/* Só existe em plano ASAAS + ANNUALLY: as duas travas estão
                                        no `AssinaturaService`, e opção que vai falhar não entra. */}
                                    {billingService.aceitaParcelamento(escolhido) && (
                                        <button type="button" className={`pln-opt ${forma === 'CARD_INSTALLMENT' ? 'is-on' : ''}`}
                                            onClick={() => setForma('CARD_INSTALLMENT')}
                                            aria-pressed={forma === 'CARD_INSTALLMENT'}>
                                            <span className="pln-rd" />
                                            <span className="pln-opt-ic"><LuCreditCard size={16} /></span>
                                            <span className="pln-opt-tx">
                                                <b>Cartão parcelado</b>
                                                <span>Compra única em até 12x. Não renova e não permite troca de plano.</span>
                                            </span>
                                        </button>
                                    )}
                                    {ehTrial && (
                                        <div className="pln-nota">
                                            <LuInfo size={14} />
                                            <span>
                                                Assim que o pagamento for confirmado, o teste grátis é encerrado
                                                e o plano <b>{escolhido.planoNome}</b> passa a valer.
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="pln-dir">
                            <div className="pln-res-t">Resumo</div>
                            {ehTrial && (
                                <div className="pln-alerta">
                                    <LuCircleAlert size={14} />
                                    <span><b>A cobrança acontece agora</b>, não no fim do teste.</span>
                                </div>
                            )}
                            <div className="pln-rl">Plano {escolhido.planoNome}<span>{brl(escolhido.planoPreco)}</span></div>
                            <div className="pln-rl">Usuários<span>× {quantidade}</span></div>
                            <div className="pln-rl">Cobrança<span>{CICLO_ADJ[escolhido.planoCiclo] ?? '—'}</span></div>
                            {/* Rótulo curto de propósito: a coluna tem 264px e o valor em
                                19px come metade — "Valor do novo plano" quebrava em duas
                                linhas. O bloco "O que muda" ao lado já diz de qual plano
                                é o valor. */}
                            <div className="pln-rl is-tot">
                                {trocaEPossivel ? 'Novo valor' : 'Total hoje'}<span>{totalHoje}</span>
                            </div>

                            <button type="button" className="pln-btn is-pri pln-btn-big"
                                disabled={processando || semMudanca || (pedeDocumento && !documentoCompleto)}
                                onClick={() => (trocaEPossivel ? setConfirmarTroca(true) : contratar())}>
                                {processando
                                    ? <><LuLoaderCircle size={14} className="pln-girando" /> Aguarde…</>
                                    : soQuantidade
                                    ? <>Confirmar alteração</>
                                    : trocaEPossivel
                                    ? <>Confirmar troca</>
                                    : <><LuArrowUpRight size={14} /> Ir para o pagamento</>}
                            </button>
                            {!trocaEPossivel && (
                                <div className="pln-sob">
                                    Você será levado para o pagamento, em uma nova aba, para concluir.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <Dialog
                open={confirmarTroca}
                onClose={() => setConfirmarTroca(false)}
                onConfirm={trocar}
                title={soQuantidade
                    ? `Passar para ${usuariosTxt(quantidade)}?`
                    : `Mudar para o plano «${escolhido?.planoNome ?? ''}»?`}
                confirmLabel={soQuantidade ? 'Alterar usuários' : 'Mudar de plano'}
                cancelLabel="Continuar aqui"
                busy={processando}
            >
                {assinatura?.assinaturaProvedorPagamento === 'ASAAS'
                    ? 'A assinatura atual é cancelada agora e a nova só vale depois que você confirmar o pagamento. Nesse intervalo a empresa fica sem cobrança ativa.'
                    : 'A mudança é aplicada no próximo ciclo de cobrança. O valor atual continua valendo até lá.'}
            </Dialog>
        </div>,
        document.body,
    );
};

export default PlanoModal;
